import React, { useState, Fragment } from 'react';
import { EditIcon, TrashIcon, EyeIcon, XCircleIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, BellIcon, SettingsIcon, RefreshCwIcon, ClipboardIcon } from 'lucide-react';
import { Message } from './MessagesManagement';
import { StatusBadge } from '../../StatusBadge';
interface MessagesListProps {
  messages: Message[];
  canEditMessage: (message: Message) => boolean;
  onEdit: (message: Message) => void;
  onDelete: (id: string) => void;
  onResetOverride: (id: string) => void;
  locale: string;
}
export function MessagesList({
  messages,
  canEditMessage,
  onEdit,
  onDelete,
  onResetOverride,
  locale
}: MessagesListProps) {
  const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getTypeIcon = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-17253" />;
      case 'Warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-500" data-id="element-17254" />;
      case 'Success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-17255" />;
      case 'Info':
        return <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-17256" />;
      case 'Notification':
        return <BellIcon className="h-5 w-5 text-purple-500" data-id="element-17257" />;
      case 'System':
        return <SettingsIcon className="h-5 w-5 text-gray-500" data-id="element-17258" />;
      default:
        return null;
    }
  };
  const getTypeColor = (type: Message['type']) => {
    switch (type) {
      case 'Error':
        return 'text-red-700 bg-red-50';
      case 'Warning':
        return 'text-amber-700 bg-amber-50';
      case 'Success':
        return 'text-green-700 bg-green-50';
      case 'Info':
        return 'text-blue-700 bg-blue-50';
      case 'Notification':
        return 'text-purple-700 bg-purple-50';
      case 'System':
        return 'text-gray-700 bg-gray-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };
  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      // Could show a toast notification here
      console.log(`Copied ${key} to clipboard`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  const toggleExpand = (id: string) => {
    if (expandedMessage === id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(id);
    }
  };
  if (messages.length === 0) {
    return <div className="text-center py-12" data-id="element-17259">
        <InfoIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-17260" />
        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-17261">
          No messages found
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17262">
          No messages match your current filters. Try adjusting your search
          criteria.
        </p>
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-17263">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-17264">
        <thead data-id="element-17265">
          <tr data-id="element-17266">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17267">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17268">
              Key & Message
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17269">
              Module
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17270">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17271">
              Usage
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17272">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-17273">
          {messages.map(message => <Fragment key={message.id} data-id="element-17274">
              <tr className={expandedMessage === message.id ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-17275">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-17276">
                  <div className="flex items-center" data-id="element-17277">
                    <span className="flex-shrink-0" data-id="element-17278">
                      {getTypeIcon(message.type)}
                    </span>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-17279">
                      {message.type}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-17280">
                  <div data-id="element-17281">
                    <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-17282">
                      <button onClick={() => handleCopyKey(message.key)} className="text-gray-500 hover:text-gray-700 mr-1" title="Copy key" data-id="element-17283">
                        <ClipboardIcon className="h-4 w-4" data-id="element-17284" />
                      </button>
                      <span className="font-mono" data-id="element-17285">{message.key}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 line-clamp-2" data-id="element-17286">
                      {message.overriddenMessage ? <span className="text-indigo-600 font-medium" data-id="element-17287">
                          {message.overriddenMessage}
                        </span> : message.defaultMessage}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-17288">
                  <div className="text-sm text-gray-900" data-id="element-17289">{message.module}</div>
                  {message.context && <div className="text-sm text-gray-500" data-id="element-17290">
                      {message.context}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-17291">
                  <div className="flex flex-col space-y-1" data-id="element-17292">
                    <StatusBadge status={message.isActive ? 'success' : 'error'} text={message.isActive ? 'Active' : 'Inactive'} data-id="element-17293" />
                    {message.isSystemLocked && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-17294">
                        System Locked
                      </span>}
                    {message.overriddenMessage && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-17295">
                        Overridden
                      </span>}
                    {message.editableByRoles.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-17296">
                        RBAC Limited
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17297">
                  <div data-id="element-17298">Count: {message.usageCount}</div>
                  <div data-id="element-17299">Last: {formatDate(message.lastUsedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17300">
                  <div className="flex justify-end space-x-2" data-id="element-17301">
                    <button onClick={() => toggleExpand(message.id)} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-17302">
                      <EyeIcon className="h-5 w-5" data-id="element-17303" />
                    </button>
                    {canEditMessage(message) && <>
                        <button onClick={() => onEdit(message)} className="text-indigo-600 hover:text-indigo-900" title="Edit message" data-id="element-17304">
                          <EditIcon className="h-5 w-5" data-id="element-17305" />
                        </button>
                        {message.overriddenMessage && <button onClick={() => onResetOverride(message.id)} className="text-amber-600 hover:text-amber-900" title="Reset override" data-id="element-17306">
                            <RefreshCwIcon className="h-5 w-5" data-id="element-17307" />
                          </button>}
                        {!message.isSystemLocked && <button onClick={() => onDelete(message.id)} className="text-red-600 hover:text-red-900" title="Delete message" data-id="element-17308">
                            <TrashIcon className="h-5 w-5" data-id="element-17309" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>
              {expandedMessage === message.id && <tr className="bg-gray-50" data-id="element-17310">
                  <td colSpan={6} className="px-6 py-4" data-id="element-17311">
                    <div className="space-y-4" data-id="element-17312">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17313">
                        <div data-id="element-17314">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-17315">
                            Default Message
                          </h4>
                          <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-17316">
                            {message.defaultMessage}
                          </div>
                        </div>
                        {message.overriddenMessage && <div data-id="element-17317">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-17318">
                              Overridden Message
                            </h4>
                            <div className={`p-3 rounded-md text-sm ${getTypeColor(message.type)}`} data-id="element-17319">
                              {message.overriddenMessage}
                            </div>
                          </div>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-17320">
                        <div data-id="element-17321">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-17322">
                            Created
                          </h4>
                          <div className="text-sm text-gray-500" data-id="element-17323">
                            <div data-id="element-17324">{formatDate(message.createdAt)}</div>
                            <div data-id="element-17325">By: {message.createdBy}</div>
                          </div>
                        </div>
                        {message.modifiedAt && <div data-id="element-17326">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-17327">
                              Last Modified
                            </h4>
                            <div className="text-sm text-gray-500" data-id="element-17328">
                              <div data-id="element-17329">{formatDate(message.modifiedAt)}</div>
                              <div data-id="element-17330">By: {message.modifiedBy}</div>
                            </div>
                          </div>}
                        <div data-id="element-17331">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-17332">
                            RBAC Permissions
                          </h4>
                          {message.editableByRoles.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-17333">
                              {message.editableByRoles.map(role => <span key={role} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-17334">
                                  {role}
                                </span>)}
                            </div> : <div className="text-sm text-gray-500" data-id="element-17335">
                              {message.isSystemLocked ? 'System locked - no editing allowed' : 'No RBAC restrictions'}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}